import { useState, useEffect, Dispatch, SetStateAction } from 'react'
import { LocalStorageKeys } from '~config/constants'

// Strongly typed generic for the local storage hook
export function useLocalStorage<T>(
    key: LocalStorageKeys,
    defaultValue: T,
): [T, Dispatch<SetStateAction<T>>, () => void, () => void] {
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            if (typeof window !== 'undefined') {
                const item = window.localStorage.getItem(key)
                // Parse JSON stored item or return defaultValue if null
                return item ? (JSON.parse(item) as T) : defaultValue
            } else {
                return defaultValue
            }
        } catch (error) {
            console.error(`Failed to parse localStorage key "${key}":`, error)
            return defaultValue
        }
    })

    // Update localStorage whenever storedValue changes
    useEffect(() => {
        try {
            if (typeof window !== 'undefined') {
                window.localStorage.setItem(key, JSON.stringify(storedValue))
            }
        } catch (error) {
            console.error(`Failed to set localStorage key "${key}":`, error)
        }
    }, [key, storedValue])

    // Remove the item entirely from local storage
    const removeItem = () => {
        try {
            if (typeof window !== 'undefined') {
                window.localStorage.removeItem(key)
                setStoredValue(defaultValue)
            }
        } catch (error) {
            console.error(`Failed to remove localStorage key "${key}":`, error)
        }
    }

    // Reset to the default value (helpful if you don't want to remove it entirely)
    const resetItem = () => {
        setStoredValue(defaultValue)
    }

    return [storedValue, setStoredValue, removeItem, resetItem]
}

/**
 * Envelope persisted by {@link useLocalStorageWithTTL}. The wrapped `value`
 * is only considered valid until `expiresAt` (epoch ms).
 */
interface TTLEnvelope<T> {
    value: T
    expiresAt: number
}

function isTTLEnvelope<T>(parsed: unknown): parsed is TTLEnvelope<T> {
    return (
        typeof parsed === 'object' &&
        parsed !== null &&
        'value' in parsed &&
        typeof (parsed as { expiresAt?: unknown }).expiresAt === 'number'
    )
}

/**
 * Like {@link useLocalStorage}, but the stored value expires after `ttlMs` of
 * inactivity. The expiry slides forward every time the hook mounts or the
 * value changes, so the persisted value survives as long as the feature is
 * accessed within each TTL window — and falls back to `defaultValue` once a
 * full window passes without access.
 *
 * Legacy/non-envelope values written by other code are treated as expired, so
 * switching a key over to this hook also cleanly resets any stale data.
 */
export function useLocalStorageWithTTL<T>(
    key: LocalStorageKeys,
    defaultValue: T,
    ttlMs: number,
): [T, Dispatch<SetStateAction<T>>, () => void] {
    const [storedValue, setStoredValue] = useState<T>(() => {
        try {
            if (typeof window === 'undefined') return defaultValue

            const item = window.localStorage.getItem(key)
            if (!item) return defaultValue

            const parsed: unknown = JSON.parse(item)
            if (!isTTLEnvelope<T>(parsed) || Date.now() > parsed.expiresAt) {
                return defaultValue
            }
            return parsed.value
        } catch (error) {
            console.error(`Failed to parse localStorage key "${key}":`, error)
            return defaultValue
        }
    })

    // Persist the value with a refreshed (sliding) expiry. Runs on mount and
    // whenever the value changes, so simply opening the feature renews the TTL.
    useEffect(() => {
        try {
            if (typeof window === 'undefined') return

            const envelope: TTLEnvelope<T> = {
                value: storedValue,
                expiresAt: Date.now() + ttlMs,
            }
            window.localStorage.setItem(key, JSON.stringify(envelope))
        } catch (error) {
            console.error(`Failed to set localStorage key "${key}":`, error)
        }
    }, [key, storedValue, ttlMs])

    const removeItem = () => {
        try {
            if (typeof window !== 'undefined') {
                window.localStorage.removeItem(key)
                setStoredValue(defaultValue)
            }
        } catch (error) {
            console.error(`Failed to remove localStorage key "${key}":`, error)
        }
    }

    return [storedValue, setStoredValue, removeItem]
}
